import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { claimDailyQuest, getDailyQuests } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { DailyQuestKey } from "@/types/gameMeta"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function DailyQuestsPage() {
  const queryClient = useQueryClient()
  const { syncGameState } = useGuardiansPassport()

  const q = useQuery({
    queryKey: ["daily-quests"],
    queryFn: getDailyQuests,
  })

  const claim = useMutation({
    mutationFn: (key: DailyQuestKey) => claimDailyQuest(key),
    onSuccess: async () => {
      await syncGameState()
      void queryClient.invalidateQueries({ queryKey: ["daily-quests"] })
    },
  })

  const quests = q.data?.quests ?? []
  const counter = q.data?.counterattack

  return (
    <GameScreenLayout title="Ежедневные задания" backTo="/play">
      {q.data?.resetAt ? (
        <p className="mb-3 text-xs text-guardians-muted">
          Сброс: {new Date(q.data.resetAt).toLocaleString("ru-RU")}
        </p>
      ) : null}

      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <ul className="space-y-3">
          {quests.map((quest) => {
            const progress =
              quest.target != null && quest.progress != null
                ? `${quest.progress}/${quest.target}`
                : null
            const canClaim = quest.completed && !quest.claimed
            return (
              <li
                key={quest.questKey}
                className="rounded-xl border border-white/10 bg-white/5 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{quest.title}</p>
                    {quest.description ? (
                      <p className="mt-0.5 text-xs text-guardians-muted">
                        {quest.description}
                      </p>
                    ) : null}
                    {progress ? (
                      <p className="mt-1 text-xs text-guardians-accent">
                        Прогресс: {progress}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-guardians-muted">
                      +{quest.rewardCoins} монет
                      {quest.rewardChakra ? `, +${quest.rewardChakra} чакры` : ""}
                    </p>
                  </div>
                  <span
                    className={clsx(
                      "shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase",
                      quest.claimed
                        ? "bg-white/10 text-guardians-muted"
                        : quest.completed
                          ? "bg-green-500/20 text-green-200"
                          : "bg-white/10 text-guardians-muted"
                    )}
                  >
                    {quest.claimed ? "получено" : quest.completed ? "готово" : "в процессе"}
                  </span>
                </div>
                {canClaim ? (
                  <button
                    type="button"
                    disabled={claim.isPending}
                    onClick={() => claim.mutate(quest.questKey)}
                    className="mt-3 w-full rounded-lg bg-guardians-accent py-2 text-xs font-medium text-guardians-bg"
                  >
                    Забрать награду
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {counter ? (
        <section className="mt-6 rounded-xl border border-amber-500/30 p-3">
          <p className="text-sm font-medium">{counter.title}</p>
          <p className="text-xs text-guardians-muted">
            Контратака — награда через экран фракции (API faction/claim-counterattack).
          </p>
        </section>
      ) : null}

      {claim.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(claim.error, "Не удалось забрать награду")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
